import * as React from 'react';
import {Dropdown, Image, Modal, Tab} from 'semantic-ui-react';

import printImg from '../../assets/print.png';
import {useReportPageContext} from '../../pages/ReportPage/ReportPageContext';
import * as Analytics from '../../services/analytics';
import {useApolloClient, useSelector} from '../../state/hooks';
import {type ReportViewRef} from '../../state/reports/types';
import * as ReportTypes from '../../state/reports/types';
import {ensureAllPanelsRendered} from '../../util/report/export';
import {
  adjustContainerElementsToAvoidPageBreaks,
  calculateOffsetFromAtomicElements,
  cleanUpHtmlCardScaling,
  cleanUpPageBreakAdjustments,
  createPdfCssPageStyle,
  findAtomicElements,
  getPdfPageHeightPx,
  measurePanelGridWidthPx,
  scaleHtmlCardsForPrint,
} from '../../util/report/pdf';
import wait from '../../util/wait';

type ReportExportModalProps = {
  onLoadingChange?: (loading: boolean) => void;
  reportViewRef: ReportViewRef | null;
  open: boolean;
  onClose: () => void;
};

export const ReportExportModal = ({
  reportViewRef,
  onLoadingChange,
  open,
  onClose,
}: ReportExportModalProps) => {
  const {
    entityName,
    projectName = '',
    isPublicProject,
  } = useReportPageContext();

  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);
  const client = useApolloClient();
  const viewsState = useSelector(state => state.views);

  const downloadLaTeX = async (
    template: 'default' | 'reproducibilityChallenge'
  ) => {
    if (reportViewRef != null) {
      Analytics.reportDownloadClicked({
        reportMode:
          reportViewRef?.type === ReportTypes.REPORT_DRAFT_VIEW_TYPE
            ? 'edit'
            : 'view',
        downloadOption: 'latex',
        downloadMode:
          template === 'default'
            ? 'default template'
            : 'reproducibility challenge template',
        isPublicProject,
      });

      setLoading(true);
      const ReportExport = await import('../../util/report/export');
      await ReportExport.exportReportAsLaTeX({
        entityName,
        projectName,
        client,
        viewsState,
        reportViewRef,
        template,
      });
      setLoading(false);
      onClose();
    }
  };
  async function print(mode: 'landscape' | 'portrait') {
    Analytics.reportDownloadClicked({
      reportMode:
        reportViewRef?.type === ReportTypes.REPORT_DRAFT_VIEW_TYPE
          ? 'edit'
          : 'view',
      downloadOption: 'pdf',
      downloadMode: mode,
      isPublicProject,
    });

    setLoading(true);
    const screenGridWidthPx = measurePanelGridWidthPx();
    document
      .querySelector('.report-page')
      ?.classList.add('report-width-print-' + mode);
    let style = document.querySelector('#print-page');
    if (style == null) {
      style = document.createElement('style');
      style.id = 'print-page';
      document.head.appendChild(style);
    }

    // Couldn't find a way to do this in pure CSS :(
    style.innerHTML = createPdfCssPageStyle(mode);

    // TODO(WB-28907): These delays are intended to allow time for panels to
    // load and/or resize prior to exporting. Rather than rely on fixed delays,
    // add a way to wait on panel state stabilizing.

    try {
      await wait(500);
      window.dispatchEvent(new Event('resize'));

      await wait(500);
      await ensureAllPanelsRendered();

      scaleHtmlCardsForPrint(screenGridWidthPx);
      adjustElementsToAvoidPageBreaks(mode);

      onClose();
      setLoading(false);

      await wait(500);
      window.print(); // Blocks until dialog is closed.
    } catch (e) {
      console.error(`Error exporting report to PDF: ${e}`);
    } finally {
      await cleanUpAfterPrint();
    }
  }
  const panes = [
    {
      menuItem: 'PDF',
      render: () => (
        <Tab.Pane active attached={false}>
          <Image ui src={printImg} size="medium" rounded floated="left" />
          <p>
            Select landscape or portrait below to open your system print dialog.
          </p>
          <p>
            Choose "Save as PDF" from Destination and ensure you un-select
            "Headers and footers" under "More settings".
          </p>
          <Dropdown
            text="Print PDF"
            button
            simple
            item
            loading={loading}
            options={[
              {
                key: 'portrait',
                text: 'Portrait',
                onClick: () => print('portrait'),
              },
              {
                key: 'landscape',
                text: 'Landscape',
                onClick: () => print('landscape'),
              },
            ]}
          />
          <br style={{clear: 'both'}} />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'LaTeX',
      render: () => (
        <Tab.Pane active attached={false}>
          <p>
            Clicking "Generate LaTeX" will generate a zip file that can be
            uploaded to a service such as{' '}
            <a href="https://overleaf.com">Overleaf</a> for further editing.
          </p>
          <Dropdown
            text="Generate laTeX"
            button
            simple
            item
            loading={loading}
            options={[
              {
                key: 'default',
                text: 'Default Template',
                onClick: () => downloadLaTeX('default'),
              },
              {
                key: 'reproducibilityChallenge',
                text: 'Reproducibility Challenge Template',
                onClick: () => downloadLaTeX('reproducibilityChallenge'),
              },
            ]}
          />
        </Tab.Pane>
      ),
    },
  ];
  return (
    <Modal open={open} onClose={onClose} size={'small'}>
      <Modal.Header>Download Report</Modal.Header>
      <Modal.Content>
        <Tab menu={{secondary: true, pointing: true}} panes={panes} />
      </Modal.Content>
    </Modal>
  );
};

/**
 * Repositions report elements to avoid page breaks when exporting to PDF. Panel
 * grids are actively repositioned via CSS transforms. Other report elements
 * contain "atomic" children (images, headings, etc.) that browsers push to the
 * next page rather than splitting. We predict this browser behavior so that
 * subsequent panel grids are correctly positioned.
 */
function adjustElementsToAvoidPageBreaks(mode: 'landscape' | 'portrait') {
  const reportPage = document.querySelector('.report-page');
  if (!reportPage) {
    return;
  }

  const pageHeightPx = getPdfPageHeightPx(mode);
  const pageTopPx = reportPage.getBoundingClientRect().top;
  const slateElements = findSlateElements();

  // Document height that the browser will add repositioning "atomic" elements.
  let accumulatedOffsetPx = 0;

  for (const slateElement of slateElements) {
    // Manually reposition panel grids to avoid page breaks. This is required
    // because panels are absolutely positioned within the document.
    if (isPanelGrid(slateElement)) {
      const panels = findPanelsInElement(slateElement);
      adjustContainerElementsToAvoidPageBreaks(
        slateElement,
        panels,
        pageHeightPx,
        pageTopPx,
        accumulatedOffsetPx
      );

      continue;
    }

    // In order to maintain accurate vertical position information for panel
    // grids, browser/CSS-based repositioning needs to be accounted for. This is
    // done by finding all HTML elements for the report element which the
    // browser will push to the next page and predicting that additional height.
    const atomicElements = findAtomicElements(slateElement);
    accumulatedOffsetPx = calculateOffsetFromAtomicElements(
      atomicElements,
      pageHeightPx,
      pageTopPx,
      accumulatedOffsetPx
    );
  }
}

function isPanelGrid(element: HTMLElement): boolean {
  return element.querySelector('.panel-bank') != null;
}

async function cleanUpAfterPrint() {
  const reportPage = document.querySelector('.report-page');
  if (!reportPage) {
    return;
  }

  reportPage.classList.remove('report-width-print-landscape');
  reportPage.classList.remove('report-width-print-portrait');

  cleanUpPageBreakAdjustments();
  cleanUpHtmlCardScaling();

  await wait(500);
  window.dispatchEvent(new Event('resize'));
}

function findSlateElements(): HTMLElement[] {
  return Array.from(
    document.querySelector('[data-test="report-editable-content"]')
      ?.childNodes ?? []
  ) as HTMLElement[];
}

function findPanelsInElement(element: HTMLElement): HTMLElement[] {
  return Array.from(
    element.querySelectorAll('.panel-bank__panel')
  ) as HTMLElement[];
}
